'use client';
import { Toaster } from '@ethsign/ui';
import Bowser from 'bowser';
import '../styles/styles.css';

import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { LoadingProvider, Spin } from '@/components/Loading';
import { AccountChangedModal, AccountSwitchModal } from '@/components/Modal/AccountChangedModal';
import { BeraChainModal } from '@/components/Modal/BeraChainModal';
import { ENVS, GTAG_ID, redirectHost } from '@/constants';
import { UserInfoProvider, useRouter, useUserInfo } from '@/hooks';
import { logout } from '@/services';
import { eventBus, Providers, WalletType } from '@/utils';
import { isMobile } from '@/utils/device';

import { globalVm } from './GlobalPageCtrl';

const AuthPage = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, needAuth } = useUserInfo();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // 移动端，跳转到移动版详情页
    const pathname = window.location.pathname;
    const searchParams = window.location.search;
    if (
      isMobile() &&
      (pathname.startsWith('/contract') ||
        pathname.startsWith('/dashboard') ||
        pathname.startsWith('/signin') ||
        pathname.startsWith('/share') ||
        pathname.startsWith('/prepare'))
    ) {
      window.location.replace(redirectHost[ENVS.VITE_APP_ENV] + pathname + searchParams);
      return;
    }
  }, []);

  useEffect(() => {
    if (user?.address) {
      window.gtag('config', GTAG_ID, {
        user_id: user?.address
      });
    }
  }, [user?.address]);

  const handleAccountsChanged = function (accounts: string[]) {
    if (accounts?.[0]?.toLowerCase() !== user?.address?.toLowerCase()) {
      setOpen(true);
    }
  };

  const removeAccountListener = () => {
    window.ethereum?.off('accountsChanged', handleAccountsChanged);
  };

  useEffect(() => {
    // 监听evm钱包账户切换
    if (!user || !needAuth || user.chainType !== WalletType.EVM || !window.ethereum) {
      removeAccountListener();
      return;
    }

    window.ethereum?.on('accountsChanged', handleAccountsChanged);

    // Cleanup
    return () => {
      removeAccountListener();
    };
  }, [user, needAuth]);

  const handleLogOut = async () => {
    await logout();
    router.replace(`/signin`);
    setOpen(false);
  };

  return (
    <>
      <AccountSwitchModal onOpenChange={setOpen} open={open} onConfirm={handleLogOut} />

      {!needAuth ? children : <Spin loading={isLoading}>{user ? children : null}</Spin>}
    </>
  );
};

const EventListener = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    window.addEventListener('message', async (event) => {
      const { data } = event;
      if (data.type) {
        eventBus.emit(data.type, event);
      }
    });
  }, []);
  return children;
};

export default function Container() {
  const vmData = globalVm.$useSnapshot();
  const [isValidBrowser, setIsValidBrowser] = useState(true);
  useEffect(() => {
    const bowser = Bowser.getParser(window.navigator.userAgent);

    const isValidBrowser = bowser.satisfies({
      chrome: '>=103',
      edge: '>=103',
      firefox: '>=103'
    });
    setIsValidBrowser(!!isValidBrowser);
  }, []);

  if (!isValidBrowser) {
    return <div>Please use the latest chrome、edge or firefox</div>;
  }
  return (
    <>
      <Providers>
        <UserInfoProvider>
          <AuthPage>
            <EventListener>
              <div className="w-full h-full overflow-auto">
                <div className="min-w-[1200px] h-full">
                  <Outlet />
                </div>
              </div>
            </EventListener>
          </AuthPage>
        </UserInfoProvider>
        <AccountChangedModal
          open={vmData.accountChangedModalVisible}
          onOpenChange={globalVm.toggleAccountChangedModal}
        />
        <BeraChainModal open={vmData.showBeraChainModal} onOpenChange={globalVm.toggleBeraChainModal} />
      </Providers>
      <Toaster />
      <LoadingProvider />
    </>
  );
}
